import { useEffect, useMemo, useState } from 'react';
import {
  personName,
  ApiError,
  api,
  type EvaluableResident,
  type MyAssessments,
  type MyProgram,
  type Rubric,
} from '../lib/api';

/**
 * The evaluator's screen, and the whole of it (D45).
 *
 * This is what a login holding nothing but `performance.record` and
 * `performance.read-own` can see. There is deliberately no roster, no block
 * board, no momentum and no cohort comparison — an evaluator names a resident,
 * answers the eight questions, and can look back at what they themselves
 * submitted. Everything else about a resident belongs to the program.
 *
 * The screen does not enforce that; the API does. What the screen does is
 * avoid offering anything it would be refused for, so a refusal is never the
 * way somebody discovers where the line is.
 *
 * A director or chief resident opening this sees the same thing, because the
 * question "what have I submitted" has one answer whoever asks it. Their wider
 * reading lives on the program screens, which need `performance.read`.
 */

const ACADEMIC_YEAR_FALLBACK = '2026-2027';

export function Evaluate({ scheduleId }: { scheduleId: string | null }) {
  const [programs, setPrograms] = useState<MyProgram[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .myPrograms()
      .then((rows) => live && setPrograms(rows))
      .catch((e: Error) => live && setProblem(e.message));
    return () => {
      live = false;
    };
  }, []);

  if (problem) return <Shell><p className="error">{problem}</p></Shell>;
  if (!programs) return <Shell><p className="muted">Loading…</p></Shell>;
  if (!programs.length) {
    return (
      <Shell>
        <p className="muted">
          You do not hold a role on any residency program yet. A program director adds you from their
          “Who runs it” screen; until then there is nothing here to do.
        </p>
      </Shell>
    );
  }

  const program = programs.find((p) => p.scheduleId === scheduleId) ?? programs[0];
  const mayRecord = program.capabilities.includes('performance.record');
  return (
    <Shell>
      <div className="gridtools">
        <div className="crumbs">
          <strong>{program.scheduleName}</strong> <span className="muted">· {program.unitName}</span>
          <span className="pill tiny">{program.label}</span>
        </div>
        {programs.length > 1 && (
          <label className="field">
            Program{' '}
            <select
              value={program.scheduleId}
              onChange={(e) => {
                const q = new URLSearchParams();
                q.set('scheduleId', e.target.value);
                window.history.pushState({}, '', `/admin/evaluate?${q}`);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
            >
              {programs.map((p) => (
                <option key={p.scheduleId} value={p.scheduleId}>{p.scheduleName}</option>
              ))}
            </select>
          </label>
        )}
      </div>
      {mayRecord ? (
        <Body program={program} />
      ) : (
        <p className="error">
          A {program.label} cannot record assessments on this program.
        </p>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="residency">
      <h1>Performance assessment</h1>
      {children}
    </section>
  );
}

function Body({ program }: { program: MyProgram }) {
  const { clientId, scheduleId } = program;
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [people, setPeople] = useState<EvaluableResident[] | null>(null);
  const [mine, setMine] = useState<MyAssessments | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [year, setYear] = useState(ACADEMIC_YEAR_FALLBACK);

  const reloadMine = () =>
    api.myAssessments(clientId, scheduleId).then(setMine).catch((e: Error) => setProblem(e.message));

  useEffect(() => {
    let live = true;
    setPeople(null);
    Promise.all([api.assessmentRubric(), api.evaluableResidents(clientId, scheduleId, year), api.myAssessments(clientId, scheduleId)])
      .then(([r, p, m]) => {
        if (!live) return;
        setRubric(r);
        setPeople(p);
        setMine(m);
      })
      .catch((e: Error) => live && setProblem(e.message));
    return () => {
      live = false;
    };
  }, [clientId, scheduleId, year]);

  if (problem) return <p className="error">{problem}</p>;
  if (!rubric || !people || !mine) return <p className="muted">Loading…</p>;

  return (
    <>
      <Form
        key={`${program.scheduleId}:${year}`}
        program={program}
        rubric={rubric}
        people={people}
        year={year}
        onYear={setYear}
        onSaved={reloadMine}
      />
      <Submitted mine={mine} />
    </>
  );
}

function Form({
  program,
  rubric,
  people,
  year,
  onYear,
  onSaved,
}: {
  program: MyProgram;
  rubric: Rubric;
  people: EvaluableResident[];
  year: string;
  onYear: (y: string) => void;
  onSaved: () => void;
}) {
  const [physicianId, setPhysicianId] = useState('');
  const [period, setPeriod] = useState(rubric.periods[0] ?? 'Q1');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [saved, setSaved] = useState<string | null>(null);

  const scaleOf = useMemo(() => {
    const byKey = new Map(rubric.scales.map((s) => [s.key, s]));
    return (questionKey: string) => byKey.get(rubric.questions.find((q) => q.key === questionKey)?.scaleKey ?? '');
  }, [rubric]);

  // Every question answered, or none of it goes — a half-filled assessment is
  // not evidence, and the API refuses one anyway.
  const unanswered = rubric.questions.filter((q) => !answers[q.key]);
  const ready = physicianId !== '' && unanswered.length === 0;

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    setProblems([]);
    setSaved(null);
    try {
      await api.recordReview(program.clientId, program.scheduleId, physicianId, {
        academicYear: year,
        period,
        answers,
        note: note.trim() || null,
      });
      const who = people.find((p) => p.physicianId === physicianId);
      setSaved(`Recorded for ${who ? personName(who) : 'that resident'} — ${period}, ${year}.`);
      setAnswers({});
      setNote('');
      setPhysicianId('');
      onSaved();
    } catch (e) {
      if (e instanceof ApiError) {
        setProblem(e.message);
        const extra = (e as unknown as { body?: { problems?: string[] } }).body?.problems;
        if (Array.isArray(extra)) setProblems(extra);
      } else {
        setProblem((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card logi">
      <h2>Record an assessment</h2>
      <p className="muted">
        Eight questions, all of them. Seven ask where this resident sits against others at their own level;
        Steadiness asks something different and has its own wording.
      </p>

      <label className="field">
        <span>Resident</span>
        <select value={physicianId} onChange={(e) => setPhysicianId(e.target.value)}>
          <option value="">Choose…</option>
          {people.map((p) => (
            <option key={p.physicianId} value={p.physicianId}>
              {personName(p)} — PGY{p.pgy}
              {p.track === 'prelim' ? ' (prelim)' : ''}
            </option>
          ))}
        </select>
      </label>

      <div className="gridtools">
        <label className="field">
          <span>Period</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {rubric.periods.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Academic year</span>
          <input value={year} onChange={(e) => onYear(e.target.value)} maxLength={9} />
        </label>
      </div>

      <ol className="rubric">
        {rubric.questions.map((q) => {
          const scale = scaleOf(q.key);
          return (
            <li key={q.key}>
              <label className="field">
                <span>
                  {q.number}. {q.text}
                  {scale && <span className="muted"> — {scale.about}</span>}
                </span>
                <select value={answers[q.key] ?? ''} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}>
                  <option value="">Choose…</option>
                  {(scale?.points ?? []).map((pt) => (
                    <option key={pt.position} value={pt.label}>{pt.label}</option>
                  ))}
                </select>
              </label>
            </li>
          );
        })}
      </ol>

      <label className="field">
        <span>Note <span className="muted">— optional</span></span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} />
      </label>

      {problem && <p className="error">{problem}</p>}
      {problems.length > 0 && (
        <ul className="error">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {saved && <p className="ok small">{saved}</p>}

      <div className="formactions">
        <button type="button" className="primary" disabled={busy || !ready} onClick={submit}>
          {busy ? 'Recording…' : 'Record assessment'}
        </button>
      </div>
      {!ready && physicianId !== '' && (
        <p className="legend muted">
          {unanswered.length} question{unanswered.length === 1 ? '' : 's'} still to answer.
        </p>
      )}
      <p className="legend muted">
        A correction is a new assessment for the same period, not an edit — the earlier one stays on the
        record, and the later one is what counts.
      </p>
    </div>
  );
}

function Submitted({ mine }: { mine: MyAssessments }) {
  return (
    <div className="card logi">
      <h2>What you have submitted</h2>
      <p className="muted">
        Yours only. Another evaluator's assessment of the same resident, their history and where they sit
        against the cohort belong to the program.
      </p>
      {mine.reviews.length === 0 ? (
        <p className="muted">Nothing yet.</p>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>Resident</th>
              <th>Period</th>
              <th>Year</th>
              <th>Recorded</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {mine.reviews.map((r) => (
              <tr key={r.id}>
                <td>{personName(r.physician)}</td>
                <td>{r.period}</td>
                <td>{r.academicYear}</td>
                <td>{new Date(r.recordedAt).toLocaleDateString()}</td>
                <td className="muted">{r.note ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
